import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { BestellStatus, BestellungIntern } from "@repo/shared";

import { api, errorMessage } from "../lib/api";
import { STATUS, datum, fortschritt, statusLabel } from "../lib/bestellung";
import { euro } from "../lib/regeln";

type Filter = "alle" | "ungelesen" | BestellStatus;
type Spalte = "nummer" | "eingang" | "mandant" | "vk" | "gewinn" | "lieferung" | "status";

const PRO_SEITE = 25;

const vergleich: Record<Spalte, (a: BestellungIntern, b: BestellungIntern) => number> = {
  nummer: (a, b) => a.nummer.localeCompare(b.nummer),
  eingang: (a, b) => a.createdAt.localeCompare(b.createdAt),
  mandant: (a, b) => a.mandantName.localeCompare(b.mandantName, "de"),
  vk: (a, b) => a.summeNettoCents - b.summeNettoCents,
  gewinn: (a, b) => a.rohertragCents - b.rohertragCents,
  lieferung: (a, b) =>
    (a.voraussichtlicheLieferungAm ?? "9999").localeCompare(b.voraussichtlicheLieferungAm ?? "9999"),
  status: (a, b) =>
    STATUS.findIndex((s) => s.wert === a.status) - STATUS.findIndex((s) => s.wert === b.status),
};

export function BestellungenPage() {
  const navigate = useNavigate();
  const [bestellungen, setBestellungen] = useState<BestellungIntern[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [suche, setSuche] = useState("");
  const [filter, setFilter] = useState<Filter>("alle");
  const [sortierung, setSortierung] = useState<{ spalte: Spalte; ab: boolean }>({ spalte: "eingang", ab: true });
  const [seite, setSeite] = useState(0);

  const laden = useCallback(async () => {
    try {
      const res = await api.api.admin.bestellungen.$get();
      if (!res.ok) throw new Error(await errorMessage(res, "Bestellungen nicht ladbar"));
      const daten = await res.json();
      if ("bestellungen" in daten) setBestellungen(daten.bestellungen);
      setFehler(null);
    } catch (cause) {
      setFehler(cause instanceof Error ? cause.message : "Laden fehlgeschlagen");
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function umschaltenGelesen(b: BestellungIntern) {
    const res = await api.api.admin.bestellungen[":id"].gelesen.$put({
      param: { id: b.id },
      json: { gelesen: !b.gelesenAm },
    });
    if (!res.ok) {
      setFehler(await errorMessage(res, "Konnte nicht markiert werden"));
      return;
    }
    const daten = await res.json();
    if ("bestellung" in daten) {
      setBestellungen((liste) => liste.map((x) => (x.id === b.id ? daten.bestellung : x)));
    }
  }

  const gefiltert = useMemo(() => {
    const begriff = suche.trim().toLowerCase();
    const treffer = bestellungen.filter((b) => {
      if (filter === "ungelesen" && b.gelesenAm) return false;
      if (filter !== "alle" && filter !== "ungelesen" && b.status !== filter) return false;
      if (!begriff) return true;
      return [b.nummer, b.mandantName, b.externeReferenz ?? "", b.lieferadresse.name ?? "", b.lieferadresse.ort]
        .join(" ")
        .toLowerCase()
        .includes(begriff);
    });
    const richtung = sortierung.ab ? -1 : 1;
    return [...treffer].sort((a, b) => richtung * vergleich[sortierung.spalte](a, b));
  }, [bestellungen, suche, filter, sortierung]);

  useEffect(() => setSeite(0), [suche, filter]);

  if (laedt) return <p className="muted">Lade Bestellungen …</p>;

  const ungelesen = bestellungen.filter((b) => !b.gelesenAm).length;
  const gewinnGesamt = gefiltert
    .filter((b) => b.status !== "storniert")
    .reduce((s, b) => s + b.rohertragCents, 0);
  const seiten = Math.max(1, Math.ceil(gefiltert.length / PRO_SEITE));
  const sichtbar = gefiltert.slice(seite * PRO_SEITE, (seite + 1) * PRO_SEITE);

  const kopf = (spalte: Spalte, label: string, num = false) => (
    <th className={num ? "num" : undefined} aria-sort={sortierung.spalte === spalte ? (sortierung.ab ? "descending" : "ascending") : "none"}>
      <button
        type="button"
        className="sortierkopf"
        onClick={() =>
          setSortierung((s) => ({ spalte, ab: s.spalte === spalte ? !s.ab : true }))
        }
      >
        {label}
        <span aria-hidden="true">{sortierung.spalte === spalte ? (sortierung.ab ? " ↓" : " ↑") : ""}</span>
      </button>
    </th>
  );

  const anzahl = (f: Filter) =>
    f === "alle"
      ? bestellungen.length
      : f === "ungelesen"
        ? ungelesen
        : bestellungen.filter((b) => b.status === f).length;

  return (
    <section>
      <div className="page-head">
        <h1>
          Bestellungen
          {ungelesen > 0 && <span className="zaehler">{ungelesen} neu</span>}
        </h1>
      </div>
      <p className="muted small">
        Eingang über die öffentliche API. Einkaufswert und Gewinn sind nur hier sichtbar.
      </p>

      {fehler && <p className="error">{fehler}</p>}

      <div className="werkzeugleiste">
        <input
          type="search"
          placeholder="Nummer, Mandant, Referenz, Ort …"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          aria-label="Bestellungen durchsuchen"
        />
        <div className="filter" role="tablist" aria-label="Filter">
          {(["alle", "ungelesen", ...STATUS.map((s) => s.wert)] as Filter[]).map((f) => (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={filter === f}
              className={filter === f ? "filter__chip filter__chip--aktiv" : "filter__chip"}
              onClick={() => setFilter(f)}
            >
              {f === "alle" ? "Alle" : f === "ungelesen" ? "Ungelesen" : statusLabel(f)}
              <span className="filter__anzahl">{anzahl(f)}</span>
            </button>
          ))}
        </div>
      </div>

      {gefiltert.length === 0 ? (
        <p className="muted centered">
          {bestellungen.length === 0 ? "Noch keine Bestellungen." : "Keine Bestellung passt zum Filter."}
        </p>
      ) : (
        <>
          <p className="muted small">
            {gefiltert.length} Bestellungen · Gewinn <strong>{euro(gewinnGesamt)}</strong> (ohne stornierte)
          </p>
          <div className="tabelle-rahmen">
            <table className="table table--daten">
              <thead>
                <tr>
                  <th aria-label="Gelesen" />
                  {kopf("nummer", "Nummer")}
                  {kopf("eingang", "Eingang")}
                  {kopf("mandant", "Mandant")}
                  <th>Hersteller</th>
                  {kopf("vk", "VK netto", true)}
                  {kopf("gewinn", "Gewinn", true)}
                  {kopf("lieferung", "Lieferung")}
                  {kopf("status", "Status")}
                </tr>
              </thead>
              <tbody>
                {sichtbar.map((b) => {
                  const f = fortschritt(b);
                  const klassen = [
                    "zeile--klickbar",
                    b.gelesenAm ? "" : "zeile--ungelesen",
                    b.status === "storniert" ? "row--muted" : "",
                  ].join(" ");
                  return (
                    <tr key={b.id} className={klassen} onClick={() => navigate(`/bestellungen/${b.id}`)}>
                      <td>
                        <button
                          type="button"
                          className="gelesen-punkt"
                          title={b.gelesenAm ? "Als ungelesen markieren" : "Als gelesen markieren"}
                          aria-label={b.gelesenAm ? "Als ungelesen markieren" : "Als gelesen markieren"}
                          onClick={(e) => {
                            e.stopPropagation();
                            void umschaltenGelesen(b);
                          }}
                        >
                          <span className={b.gelesenAm ? "punkt" : "punkt punkt--neu"} />
                        </button>
                      </td>
                      <td>
                        <span className="nummer">{b.nummer}</span>
                        {b.externeReferenz && <div className="muted small">{b.externeReferenz}</div>}
                      </td>
                      <td className="small">{datum(b.createdAt)}</td>
                      <td className="small">
                        {b.mandantName}
                        <div className="muted">
                          {b.lieferadresse.plz} {b.lieferadresse.ort}
                        </div>
                      </td>
                      <td className="small">
                        <span className={f.bestellt === f.gesamt ? "fortschritt fortschritt--fertig" : "fortschritt"}>
                          {f.bestellt}/{f.gesamt} bestellt
                        </span>
                        <div className="muted">{b.hersteller.map((h) => h.name).join(", ")}</div>
                      </td>
                      <td className="num">{euro(b.summeNettoCents)}</td>
                      <td className="num">
                        <strong>{euro(b.rohertragCents)}</strong>
                      </td>
                      <td className="small">{datum(b.voraussichtlicheLieferungAm)}</td>
                      <td>
                        <span className={`badge badge--status-${b.status}`}>{statusLabel(b.status)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {seiten > 1 && (
            <div className="actions blaetterung">
              <button type="button" disabled={seite === 0} onClick={() => setSeite((s) => s - 1)}>
                Zurück
              </button>
              <span className="muted small">
                Seite {seite + 1} von {seiten}
              </span>
              <button type="button" disabled={seite + 1 >= seiten} onClick={() => setSeite((s) => s + 1)}>
                Weiter
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
